import { apiFetch } from '../services/api';
/**
 * useGamenics — ゲームニクスシステムのReactフック
 *
 * GamenicsManagerのシングルトンをReactコンポーネントから利用するためのフック。
 * アプリ起動時に自動的にconfig読み込みを行い、
 * 全コンポーネントからSE再生・褒める演出・習熟度情報にアクセスできるようにする。
 *
 * 使用例:
 *   const { playSound, triggerCelebration } = useGamenics();
 *   playSound(GamenicsEvent.TAP_CONFIRM);
 *   triggerCelebration(GamenicsEvent.MILESTONE_10);
 *
 * @see docs/GAMENICS_DESIGN_GUIDELINES.md
 */

import { useEffect, useCallback, useState } from 'react';
import {
  GamenicsManager,
  GamenicsEvent,
  type GamenicsEventKey,
  type UserProficiency,
  type UserGoal,
  ProficiencyLevel,
} from '../services/gamenics';

// ============================================================
// useGamenics — メインフック
// ============================================================

export function useGamenics() {
  const [isReady, setIsReady] = useState(GamenicsManager.loaded);

  // アプリ起動時にconfig自動読み込み
  useEffect(() => {
    if (!GamenicsManager.loaded) {
      GamenicsManager.loadConfig().then(() => setIsReady(true));
    }
  }, []);

  /**
   * SE再生（全タップ操作に使用）
   * ガイドライン: 無音操作は一切禁止
   */
  const playSound = useCallback((eventKey: GamenicsEventKey | string) => {
    GamenicsManager.playSound(eventKey);
  }, []);

  /**
   * 「褒める」演出（間 + SE + エフェクト同期）
   * ガイドライン: 褒める前に一瞬全てを止める
   */
  const triggerCelebration = useCallback((eventKey: GamenicsEventKey | string) => {
    return GamenicsManager.triggerCelebration(eventKey);
  }, []);

  /**
   * 可変報酬演出（フックモデル）
   * ガイドライン: 報酬のタイミング・内容を予測不可能にする
   */
  const triggerVariableReward = useCallback(
    (rewards: Array<{ type: string; data: unknown }>) => {
      return GamenicsManager.triggerVariableReward(rewards);
    }, []
  );

  /**
   * 可変報酬バッチ用のランダム遅延を取得
   */
  const getRandomBatchDelay = useCallback(() => {
    return GamenicsManager.getRandomBatchDelay();
  }, []);

  /**
   * 設定リロード（Admin画面で設定変更後）
   */
  const reloadConfig = useCallback(async () => {
    GamenicsManager.clearCache();
    await GamenicsManager.loadConfig();
    setIsReady(true);
  }, []);

  return {
    isReady,
    playSound,
    triggerCelebration,
    triggerVariableReward,
    getRandomBatchDelay,
    reloadConfig,
    GamenicsEvent,
  };
}

// ============================================================
// useUserProficiency — 習熟度フック
// ============================================================

export function useUserProficiency() {
  const [proficiency, setProficiency] = useState<UserProficiency | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/user/proficiency');
        if (!res.ok) {
          // 【フェイルセーフ】APIなし → デフォルト値
          setProficiency(null);
          return;
        }
        const data = await res.json();
        if (data.success && data.data) {
          setProficiency({
            totalSubmissions: data.data.total_submissions ?? 0,
            totalLikesReceived: data.data.total_likes_received ?? 0,
            totalVotesCast: data.data.total_votes_cast ?? 0,
            proficiencyLevel: data.data.proficiency_level ?? ProficiencyLevel.BEGINNER,
            badges: JSON.parse(data.data.badges_json ?? '[]'),
            streakDays: data.data.streak_days ?? 0,
            streakMaxDays: data.data.streak_max_days ?? 0,
            profileCompletionPercent: data.data.profile_completion_percent ?? 0,
          });
        }
      } catch (e) {
        // 【フェイルセーフ】取得失敗は無視
        console.warn('[Gamenics] Proficiency fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * 習熟度レベルに基づいたUI表示判定
   *
   * ガイドライン:
   * - Level 1 (Beginner): 丁寧なヘルプ、メニュー3つ
   * - Level 2 (Intermediate): ヘルプ簡略化、新メニュー追加
   * - Level 3 (Advanced): ヘルプ非表示、全メニュー開放
   */
  const shouldShowHelp = proficiency
    ? proficiency.proficiencyLevel < ProficiencyLevel.ADVANCED
    : true; // 未取得時は初心者扱い

  const shouldShowDetailedHelp = proficiency
    ? proficiency.proficiencyLevel === ProficiencyLevel.BEGINNER
    : true;

  const unlockedMenuItems = proficiency
    ? getUnlockedMenuItems(proficiency.proficiencyLevel)
    : ['home', 'record']; // 初心者デフォルト

  return {
    proficiency,
    loading,
    shouldShowHelp,
    shouldShowDetailedHelp,
    unlockedMenuItems,
  };
}

/**
 * 習熟度レベルに応じて解放されるメニュー項目
 * ガイドライン: 「上達するごとに、トップメニューの数が増えていく」
 */
function getUnlockedMenuItems(level: number): string[] {
  const base = ['home', 'record']; // 初心者: 2つ
  if (level >= ProficiencyLevel.INTERMEDIATE) {
    base.push('contests'); // 中級者: コンテスト参加追加
  }
  if (level >= ProficiencyLevel.ADVANCED) {
    base.push('studio'); // 上級者: クリエイタースタジオ全開放
  }
  return base;
}

// ============================================================
// useUserGoals — 目標フック
// ============================================================

export function useUserGoals() {
  const [goals, setGoals] = useState<UserGoal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/user/goals');
        if (!res.ok) {
          setGoals([]);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setGoals(data.data.map((g: any) => ({
            id: g.id,
            goalType: g.goal_type,
            displayText: g.display_text,
            targetValue: g.target_value,
            currentValue: g.current_value,
            isCompleted: !!g.is_completed,
            priority: g.priority,
          })));
        }
      } catch (e) {
        // 【フェイルセーフ】取得失敗は空配列
        console.warn('[Gamenics] Goals fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ガイドライン: 「常に5項目の目標のうち、3項目は直近目標」
  // ホーム画面には直近目標を上位3つ提示する
  const nearTermGoals = goals
    .filter(g => !g.isCompleted)
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 3);

  const allActiveGoals = goals.filter(g => !g.isCompleted);

  return {
    goals,
    nearTermGoals,
    allActiveGoals,
    loading,
  };
}

// Re-export for convenience
export {
  GamenicsEvent,
  ProficiencyLevel,
  calculateProficiencyLevel,
} from '../services/gamenics';

export type { PsychologyParams } from '../services/gamenics';

// ============================================================
// useStreakWarning — ストリーク警告フック（損失回避）
// ============================================================

/**
 * ストリーク喪失警告の状態を提供するフック
 *
 * ガイドライン: 利益訴求ではなく「積み上げた実績を失う痛み」を強調
 * 例: 「🔥今日の声を残さないと、連続記録Ｈ219日）が失われます」
 */
export function useStreakWarning(proficiency: UserProficiency | null) {
  const [warning, setWarning] = useState<{
    shouldWarn: boolean;
    message: string;
    hoursRemaining: number;
  }>({ shouldWarn: false, message: '', hoursRemaining: 0 });

  useEffect(() => {
    if (!proficiency || proficiency.streakDays <= 0) {
      setWarning({ shouldWarn: false, message: '', hoursRemaining: 0 });
      return;
    }

    // ストリーク警告を定期チェックＨ15分ごと）
    const check = () => {
      const result = GamenicsManager.checkStreakWarning(
        proficiency.streakDays,
        null // APIからlastPostDateを取得する実装は別途
      );
      setWarning(result);
    };

    check();
    const interval = setInterval(check, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [proficiency]);

  return warning;
}
